'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

const PROGRESS_STAGES = [
  { delay: 0, progress: 0.18 },
  { delay: 90, progress: 0.34 },
  { delay: 220, progress: 0.5 },
  { delay: 420, progress: 0.64 },
  { delay: 720, progress: 0.76 },
  { delay: 1200, progress: 0.84 },
  { delay: 1900, progress: 0.92 },
] as const;
const IDLE_TIMEOUT_MS = 8000;
const FINISH_HIDE_DELAY_MS = 180;
const MIN_VISIBLE_MS = 260;

function getCurrentRouteKey() {
  return `${window.location.pathname}${window.location.search}`;
}

function isHistoryDetailChange(currentKey: string, nextKey: string) {
  const current = new URL(currentKey, window.location.origin);
  const next = new URL(nextKey, window.location.origin);
  if (
    current.pathname !== next.pathname ||
    !/^\/(?:[a-z-]+\/)?history\/?$/.test(current.pathname)
  )
    return false;
  for (const key of ['job', 'batch']) {
    current.searchParams.delete(key);
    next.searchParams.delete(key);
  }
  return current.search === next.search;
}

function shouldStartForAnchorClick(event: MouseEvent) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false;
  }

  const target = event.target;
  if (!(target instanceof Element)) {
    return false;
  }

  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) {
    return false;
  }

  if (anchor.target && anchor.target !== '_self') {
    return false;
  }

  if (anchor.hasAttribute('download')) {
    return false;
  }

  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#')) {
    return false;
  }

  const nextUrl = new URL(anchor.href, window.location.href);
  if (nextUrl.origin !== window.location.origin) {
    return false;
  }

  const currentRouteKey = getCurrentRouteKey();
  const nextRouteKey = `${nextUrl.pathname}${nextUrl.search}`;

  return (
    currentRouteKey !== nextRouteKey &&
    !isHistoryDetailChange(currentRouteKey, nextRouteKey)
  );
}

function getUrlRouteKey(url: string | URL | null | undefined) {
  if (!url) {
    return null;
  }

  const nextUrl = new URL(url, window.location.href);
  if (nextUrl.origin !== window.location.origin) {
    return null;
  }

  return `${nextUrl.pathname}${nextUrl.search}`;
}

export function RouteTransitionBar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const activeRef = useRef(false);
  const startedAtRef = useRef(0);
  const routeKeyRef = useRef<string | null>(null);
  const historyRouteKeyRef = useRef<string | null>(null);
  const timersRef = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    for (const timer of timersRef.current) {
      window.clearTimeout(timer);
    }
    timersRef.current = [];
  }, []);

  const queueTimer = useCallback((handler: () => void, delay: number) => {
    const timer = window.setTimeout(handler, delay);
    timersRef.current.push(timer);
  }, []);

  const start = useCallback(() => {
    clearTimers();
    activeRef.current = true;
    startedAtRef.current = window.performance.now();
    setVisible(true);
    setProgress(PROGRESS_STAGES[0].progress);

    for (const stage of PROGRESS_STAGES.slice(1)) {
      queueTimer(() => {
        if (activeRef.current) {
          setProgress(stage.progress);
        }
      }, stage.delay);
    }

    queueTimer(() => {
      if (activeRef.current) {
        activeRef.current = false;
        setProgress(1);
        queueTimer(() => {
          setVisible(false);
          setProgress(0);
        }, FINISH_HIDE_DELAY_MS);
      }
    }, IDLE_TIMEOUT_MS);
  }, [clearTimers, queueTimer]);

  const startAfterHistoryMutation = useCallback(() => {
    window.setTimeout(() => {
      if (routeKeyRef.current !== getCurrentRouteKey()) {
        start();
      }
    }, 0);
  }, [start]);

  const finish = useCallback(() => {
    if (!activeRef.current && !visible) {
      return;
    }

    const finishDelay = Math.max(
      0,
      MIN_VISIBLE_MS - (window.performance.now() - startedAtRef.current)
    );

    queueTimer(() => {
      clearTimers();
      activeRef.current = false;
      setProgress(1);

      queueTimer(() => {
        setVisible(false);
        setProgress(0);
      }, FINISH_HIDE_DELAY_MS);
    }, finishDelay);
  }, [clearTimers, queueTimer, visible]);

  useEffect(() => {
    routeKeyRef.current = getCurrentRouteKey();
    historyRouteKeyRef.current = getCurrentRouteKey();
  }, []);

  useEffect(() => {
    const nextRouteKey = getCurrentRouteKey();
    if (routeKeyRef.current && routeKeyRef.current !== nextRouteKey) {
      routeKeyRef.current = nextRouteKey;
      finish();
      return;
    }

    routeKeyRef.current = nextRouteKey;
  }, [finish, pathname, searchParams]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (shouldStartForAnchorClick(event)) {
        start();
      }
    };

    const patchHistoryMethod = (method: 'pushState' | 'replaceState') => {
      const original = window.history[method];

      window.history[method] = function patchedHistoryMethod(
        ...args: Parameters<History[typeof method]>
      ) {
        const nextRouteKey = getUrlRouteKey(args[2]);
        if (
          nextRouteKey &&
          nextRouteKey !== routeKeyRef.current &&
          !isHistoryDetailChange(getCurrentRouteKey(), nextRouteKey)
        ) {
          startAfterHistoryMutation();
        }

        const result = original.apply(this, args);
        historyRouteKeyRef.current = getCurrentRouteKey();
        return result;
      };

      return () => {
        window.history[method] = original;
      };
    };

    document.addEventListener('pointerdown', handleDocumentClick, true);
    document.addEventListener('click', handleDocumentClick, true);
    const handlePopState = () => {
      const next = getCurrentRouteKey();
      const previous = historyRouteKeyRef.current;
      historyRouteKeyRef.current = next;
      if (!previous || !isHistoryDetailChange(previous, next)) start();
    };
    window.addEventListener('popstate', handlePopState);

    const restorePushState = patchHistoryMethod('pushState');
    const restoreReplaceState = patchHistoryMethod('replaceState');

    return () => {
      clearTimers();
      document.removeEventListener('pointerdown', handleDocumentClick, true);
      document.removeEventListener('click', handleDocumentClick, true);
      window.removeEventListener('popstate', handlePopState);
      restorePushState();
      restoreReplaceState();
    };
  }, [clearTimers, finish, start, startAfterHistoryMutation]);

  return (
    <div
      aria-hidden="true"
      data-route-transition-bar=""
      className="pointer-events-none fixed left-0 right-0 top-0 z-[2147483647] h-px overflow-hidden"
    >
      <div
        className="h-full origin-left bg-[var(--color-primary)] shadow-[0_0_12px_var(--color-primary)] transition-[transform,opacity] duration-200 ease-out"
        style={{
          opacity: visible ? 1 : 0,
          transform: `scaleX(${progress})`,
        }}
      />
    </div>
  );
}
